import { useCallback } from "react";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import { Note } from "../../../../../components/primitives/data-display/Note";
import type { FieldAnnotationState, FieldReannotationField } from "../../../../../lib/corpusApi";
import { DocumentScopeField } from "../../DocumentScopeField";
import {
  FIELD_ANNOTATION_STATE_OPTIONS,
  FIELD_REANNOTATION_FIELD_OPTIONS,
} from "./field_reannotation_fields";
import type { UseFieldReannotationParamsResult } from "./useFieldReannotationParams";

export interface FieldReannotationParamsFormProps {
  form: UseFieldReannotationParamsResult;
}

/** The params a field_reannotation job asks for: which field to re-annotate, and over which
 *  documents. */
export function FieldReannotationParamsForm({ form }: FieldReannotationParamsFormProps) {
  const { field, setField, annotationState, setAnnotationState } = form;

  const handleFieldChange = useCallback(
    (e: SelectChangeEvent) => setField(e.target.value as FieldReannotationField),
    [setField]
  );

  const handleAnnotationStateChange = useCallback(
    (e: SelectChangeEvent) => setAnnotationState(e.target.value as FieldAnnotationState),
    [setAnnotationState]
  );

  return (
    <>
      <FormControl fullWidth size="small">
        <InputLabel id="field-reannotation-field-label">Field</InputLabel>
        <Select
          labelId="field-reannotation-field-label"
          label="Field"
          value={field}
          onChange={handleFieldChange}
        >
          {FIELD_REANNOTATION_FIELD_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth size="small">
        <InputLabel id="field-reannotation-state-label">
          Only documents where the field is
        </InputLabel>
        <Select
          labelId="field-reannotation-state-label"
          label="Only documents where the field is"
          value={annotationState}
          onChange={handleAnnotationStateChange}
        >
          {FIELD_ANNOTATION_STATE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <DocumentScopeField form={form} />
      <Note>
        The AI&apos;s labels for the field are replaced on every matching document, skipping the
        admission gate. A label a person decided is never changed: where the new AI label disagrees
        with it, the job waits for you to override it or keep it.
      </Note>
    </>
  );
}
